import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Template } from '../types';
import { templateService } from '../services/api';
import './TemplateList.css';

const TemplateList: React.FC = () => {
  const [templates, setTemplates] = useState<Template[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [filter, setFilter] = useState<{ type: string; category: string }>({
    type: 'all',
    category: 'all',
  });
  
  const navigate = useNavigate();

  useEffect(() => {
    const fetchTemplates = async () => {
      try {
        setLoading(true);
        const data = await templateService.getAll();
        setTemplates(data);
        setError(null);
      } catch (err) {
        console.error('Erreur lors de la récupération des modèles:', err);
        setError('Erreur lors de la récupération des modèles. Veuillez réessayer.');
      } finally {
        setLoading(false);
      }
    };

    fetchTemplates();
  }, []);

  const handleDelete = async (id: number) => {
    if (window.confirm('Êtes-vous sûr de vouloir supprimer ce modèle ?')) {
      try {
        await templateService.delete(id);
        setTemplates(templates.filter(template => template.id !== id));
      } catch (err) {
        console.error('Erreur lors de la suppression du modèle:', err);
        setError('Erreur lors de la suppression du modèle. Veuillez réessayer.');
      }
    }
  };

  const filteredTemplates = templates.filter(template => {
    return (
      (filter.type === 'all' || template.type === filter.type) &&
      (filter.category === 'all' || template.category === filter.category)
    );
  });

  const getCategoryLabel = (category: string): string => {
    switch (category) {
      case 'confirmation':
        return 'Confirmation';
      case 'reminder':
        return 'Rappel';
      case 'cancellation':
        return 'Annulation';
      case 'other':
        return 'Autre';
      default:
        return category;
    }
  };

  return (
    <div className="template-list-container">
      <div className="template-list-header">
        <h1>Modèles de communication</h1>
        <button 
          className="btn-primary"
          onClick={() => navigate('/templates/new')}
        >
          Nouveau modèle
        </button>
      </div>

      {error && <div className="error-message">{error}</div>}

      <div className="template-filters">
        <div className="filter-group">
          <label htmlFor="type-filter">Type:</label>
          <select
            id="type-filter"
            value={filter.type}
            onChange={(e) => setFilter({ ...filter, type: e.target.value })}
          >
            <option value="all">Tous</option>
            <option value="email">Email</option>
            <option value="sms">SMS</option>
          </select>
        </div>

        <div className="filter-group">
          <label htmlFor="category-filter">Catégorie:</label>
          <select
            id="category-filter"
            value={filter.category}
            onChange={(e) => setFilter({ ...filter, category: e.target.value })}
          >
            <option value="all">Toutes</option>
            <option value="confirmation">Confirmation</option>
            <option value="reminder">Rappel</option>
            <option value="cancellation">Annulation</option>
            <option value="other">Autre</option>
          </select>
        </div>
      </div>

      {loading ? (
        <div className="loading">Chargement des modèles...</div>
      ) : filteredTemplates.length === 0 ? (
        <div className="no-templates">
          Aucun modèle trouvé. Créez votre premier modèle en cliquant sur "Nouveau modèle".
        </div>
      ) : (
        <div className="template-grid">
          {filteredTemplates.map((template) => (
            <div key={template.id} className="template-card">
              <div className="template-card-header">
                <h3>{template.name}</h3>
                <div className="template-badges">
                  <span className={`badge badge-${template.type}`}>
                    {template.type === 'email' ? 'Email' : 'SMS'}
                  </span>
                  <span className="badge badge-category">
                    {getCategoryLabel(template.category)}
                  </span>
                </div>
              </div>
              <div className="template-card-content">
                {template.type === 'email' && (
                  <div className="template-subject">
                    <strong>Sujet:</strong> {template.subject}
                  </div>
                )}
                <div className="template-preview">
                  {template.content.length > 100
                    ? `${template.content.substring(0, 100)}...`
                    : template.content}
                </div>
              </div>
              <div className="template-card-actions">
                <Link to={`/templates/${template.id}`} className="btn-secondary">
                  Voir
                </Link>
                <Link to={`/templates/${template.id}/edit`} className="btn-secondary">
                  Modifier
                </Link>
                <button
                  className="btn-danger"
                  onClick={() => handleDelete(template.id)}
                >
                  Supprimer
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default TemplateList;
